// src/pages/Login.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Button, TextField, Container, Typography, Box, Alert, CircularProgress, Link, Divider } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError('Falha ao fazer login. Verifique suas credenciais.');
    }
    setLoading(false);
  };

  const getErrorMessage = (err, fallback) => {
    const details = [err?.code, err?.message].filter(Boolean).join(' - ');
    return details ? `${fallback} ${details}` : fallback;
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
      navigate('/');
    } catch (err) {
      console.error('Erro no login com Google:', err);
      setError(getErrorMessage(err, 'Falha ao fazer login com o Google.'));
    }
    setLoading(false);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
    }}>
      <Container maxWidth="xs">
        <Box sx={{
          p: 4,
          borderRadius: '16px',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: '14px',
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mb: 2, boxShadow: '0 8px 24px rgba(99,102,241,0.4)'
            }}>
              <AccountBalanceIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Typography variant="h5" fontWeight={800}>Minha Contabilidade</Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
              Faça login para continuar
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal" required fullWidth
              label="E-mail" type="email"
              autoComplete="email" autoFocus
              value={email} onChange={e => setEmail(e.target.value)}
            />
            <TextField
              margin="normal" required fullWidth
              label="Senha" type="password"
              autoComplete="current-password"
              value={password} onChange={e => setPassword(e.target.value)}
            />
            <Button
              type="submit" fullWidth variant="contained"
              sx={{ mt: 2.5, mb: 1.5, py: 1.2 }} disabled={loading}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Entrar'}
            </Button>

            <Divider sx={{ my: 1.5 }}>
              <Typography variant="caption" color="text.secondary">ou</Typography>
            </Divider>

            <Button
              fullWidth variant="outlined"
              onClick={handleGoogleLogin} disabled={loading}
              startIcon={<GoogleIcon />}
              sx={{ py: 1.2, borderColor: 'divider', color: 'text.primary' }}
            >
              Entrar com Google
            </Button>

            <Box sx={{ mt: 2.5, textAlign: 'center' }}>
              <Link component={RouterLink} to="/signup" variant="body2" color="primary.light">
                Não tem uma conta? Cadastre-se
              </Link>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Login;
